import React, { useMemo } from 'react';
import * as Yup from 'yup';

import { GlobalAccountSettlementRule, SettlementType } from '@ndvr/billing-api';

import { parseLocalDate, tomorrowDate, useRequiredParams } from 'utils';
import { FormSection, requiredStringValidator } from 'widgets';
import { FormDateInput } from 'widgets/form/input/FormDateInput';

import { useGlobalAccountSettlementRuleTimeline, useSaveGlobalAccountSettlementRule } from '../billing-queries';
import { getDisabledDates, getFirstAvailableDate, isDateDisabled } from '../billing-utils';
import { SettlementSelect } from '../widgets/SettlementSelect';
import { BillingDrawer } from './BillingDrawer';

interface Props {
    globalAccountSettlementRule?: GlobalAccountSettlementRule;
    onClose: () => void;
}

export const SettlementProfileDrawer = ({ globalAccountSettlementRule, onClose }: Props) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { mutate: saveGlobalAccountSettlementRule, isPending } = useSaveGlobalAccountSettlementRule(
        globalAccountGUID,
        !!globalAccountSettlementRule,
    );

    const { data: timeline } = useGlobalAccountSettlementRuleTimeline(globalAccountGUID);
    const disabledDates = getDisabledDates(
        timeline?.upcomingRules.filter(
            (rule) =>
                rule.globalAccountSettlementRuleGUID !== globalAccountSettlementRule?.globalAccountSettlementRuleGUID,
        ),
    );
    const firstAvailableDate = getFirstAvailableDate(disabledDates);

    const defaultValues = useMemo(() => {
        return globalAccountSettlementRule
            ? { ...globalAccountSettlementRule }
            : ({
                  globalAccountGUID,
                  settlementForFixedFees: SettlementType.DEFAULT_SETTLEMENT,
                  settlementForVariableFees: SettlementType.DEFAULT_SETTLEMENT,
                  startDate: firstAvailableDate,
              } as GlobalAccountSettlementRule);
    }, [firstAvailableDate, globalAccountGUID, globalAccountSettlementRule]);

    const formSchema = Yup.object({
        settlementForFixedFees: requiredStringValidator,
        portfolioForFixedFees: Yup.string().when('settlementForFixedFees', {
            is: (type: SettlementType) =>
                type === SettlementType.SETTLED_BY_PORTFOLIO || type === SettlementType.SETTLED_BY_EXTERNAL_PORTFOLIO,
            then: () => requiredStringValidator.label('Account for membership fee'),
        }),
        globalAccountForFixedFees: Yup.string().when('settlementForFixedFees', {
            is: SettlementType.SETTLED_BY_EXTERNAL_PORTFOLIO,
            then: () => requiredStringValidator.label('Household for membership fee'),
        }),
        settlementForVariableFees: requiredStringValidator,
        portfolioForVariableFees: Yup.string().when('settlementForVariableFees', {
            is: (type: SettlementType) =>
                type === SettlementType.SETTLED_BY_PORTFOLIO || type === SettlementType.SETTLED_BY_EXTERNAL_PORTFOLIO,
            then: () => requiredStringValidator.label('Account for AUM charge'),
        }),
        globalAccountForVariableFees: Yup.string().when('settlementForVariableFees', {
            is: SettlementType.SETTLED_BY_EXTERNAL_PORTFOLIO,
            then: () => requiredStringValidator.label('Household for AUM charge'),
        }),
        startDate: requiredStringValidator.test(
            'start-date-available',
            'Rule already exists with this start date',
            (startDate) => !isDateDisabled(parseLocalDate(startDate), disabledDates),
        ),
    });

    return (
        <BillingDrawer
            title={globalAccountSettlementRule ? 'Update Settlement Profile' : 'Create New Settlement Profile'}
            editing={!!globalAccountSettlementRule}
            formSchema={formSchema}
            defaultValues={defaultValues}
            onSubmit={saveGlobalAccountSettlementRule}
            pending={isPending}
            onClose={onClose}
        >
            <FormSection>
                <SettlementSelect
                    title="Membership Fee"
                    names={{ type: 'settlementForFixedFees', settlementPortfolioGUID: 'portfolioForFixedFees' }}
                    globalAccountGUID={globalAccountGUID}
                    defaultSettlementDescription="distributed by AUM"
                />
            </FormSection>
            <FormSection hasDivider>
                <SettlementSelect
                    title="AUM Charge"
                    names={{ type: 'settlementForVariableFees', settlementPortfolioGUID: 'portfolioForVariableFees' }}
                    globalAccountGUID={globalAccountGUID}
                    defaultSettlementDescription="respective account pays for self"
                />
            </FormSection>
            <FormSection hasDivider>
                <FormDateInput
                    name="startDate"
                    label="Start Date"
                    minDate={tomorrowDate()}
                    description="The preceding settlement profile will terminate on this date. This settlement profile will only terminate if a future settlement profile is established."
                    disabledDates={disabledDates}
                />
            </FormSection>
        </BillingDrawer>
    );
};
